import React, { useState } from "react";
import { useTable } from "@refinedev/antd";
import { Table, Button, Form, Modal } from "antd";
import { BaseKey, useDelete, useCreate } from "@refinedev/core";
import { PageTitle } from "../../../components/PageTitle";
import { ThemedModal } from "../../../components/ThemedModal";
import { CreateConsultantForm } from "../components/CreateConsultantForm";

export const ConsultantListPage: React.FC = () => {
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [form] = Form.useForm();

  const { tableProps } = useTable({
    resource: "consultants",
    pagination: { pageSize: 10, mode: "client" },
    sorters: { mode: "off" },
    meta: {
      fields: ["id", "firstName", "lastName", "email"],
    },
  });

  const { mutate: deleteConsultant } = useDelete();
  const { mutate: createConsultant, isLoading: isCreating } = useCreate();

  const showDeactivateConfirm = (id: BaseKey, firstName: string, lastName: string) => {
    const name = `${firstName || ""} ${lastName || ""}`.trim();
    ThemedModal.confirm({
      title: "Berater:in Deaktivieren",
      content: `Bist du sicher, dass du ${name} deaktivieren möchtest?`,
      okText: "Ja, deaktivieren",
      cancelText: "Abbrechen",
      onOk: () => {
        deleteConsultant({
          resource: "consultants",
          id,
          successNotification: {
            message: "Berater:in wurde deaktiviert.",
            type: "success",
          },
        });
      },
    });
  };

  const handleCreateConsultant = async () => {
    const values = await form.validateFields();
    createConsultant(
      {
        resource: "consultants",
        meta: {
          operation: "whitelistConsultant",
          inputType: "WhitelistConsultantInput!",
        },
        values,
        successNotification: {
          message: "Du kannst die Person jederzeit innerhalb der Tabelle deaktivieren.",
          description: "Berater:in erfolgreich hinzugefügt",
          type: "success",
        },
        errorNotification: {
          message: "Berater:in konnte nicht hinzugefügt werden.",
          description: "Bitte überprüfe die eingegebenen Daten und versuche es erneut.",
          type: "error",
        },
      },
      {
        onSuccess: () => {
          setIsCreateModalVisible(false);
          form.resetFields();
        },
      }
    );
  };

  return (
    <div>
      <PageTitle title="Berater:innen" />
      <div style={{ marginBottom: "28px", textAlign: "right" }}>
        <Button type="primary" size="large" onClick={() => setIsCreateModalVisible(true)}>
          Berater:in hinzufügen
        </Button>
      </div>
      <Table
        {...tableProps}
        rowKey="id"
        columns={[
          {
            title: "Name",
            key: "name",
            render: (_, record) => {
              const firstName = record.firstName || "";
              const lastName = record.lastName || "";
              return `${firstName} ${lastName}`.trim();
            },
          },
          {
            title: "Email",
            dataIndex: "email",
            key: "email",
          },
          {
            title: "Aktionen",
            key: "actions",
            render: (_, record) => (
              <Button
                onClick={() => record.id && showDeactivateConfirm(record.id, record.firstName, record.lastName)}
                type="link"
              >
                Deaktivieren
              </Button>
            ),
          },
        ]}
      />

      <Modal
        title="Berater:in hinzufügen"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={handleCreateConsultant}
        okText="Hinzufügen"
        cancelText="Abbrechen"
        okButtonProps={{ size: "large", loading: isCreating }}
        cancelButtonProps={{ size: "large" }}
      >
        <CreateConsultantForm form={form} />
      </Modal>
    </div>
  );
};
